import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, X } from "lucide-react";
import { api, apiError } from "@/lib/api";

const STATUS_COLORS = {
  New: "bg-blue-50 text-blue-700", Contacted: "bg-amber-50 text-amber-700", Pending: "bg-orange-50 text-orange-700",
  Booked: "bg-green-50 text-green-700", Completed: "bg-emerald-50 text-emerald-700", Declined: "bg-red-50 text-red-700",
  "Follow Up": "bg-amber-50 text-amber-700", "Active Client": "bg-green-50 text-green-700",
  Closed: "bg-emerald-50 text-emerald-700", "Not Interested": "bg-red-50 text-red-700",
};

const inputCls = "w-full border border-[#E5E5E2] px-4 py-3 text-sm outline-none focus:border-[#1A1A1A]";
const PAYMENT_METHODS = ["Cash", "Zelle", "Venmo", "Card", "Check", "Other"];
const TIME_OPTIONS = (() => {
  const opts = [];
  for (let h = 6; h <= 21; h++) {
    for (const m of [0, 30]) {
      const hr12 = ((h + 11) % 12) + 1;
      const ampm = h < 12 ? "AM" : "PM";
      opts.push(`${hr12}:${m === 0 ? "00" : "30"} ${ampm}`);
    }
  }
  return opts;
})();
const DURATION_OPTIONS = ["30 min", "1 hr", "1.5 hr", "2 hr", "2.5 hr", "3 hr", "4 hr", "5 hr", "6 hr", "Full day"];

// Money input: always shows a "$" before the number (while typing too), stores only the digits.
function MoneyField({ value, onChange, placeholder, testId }) {
  return (
    <div className="relative">
      {value && (
        <span className="absolute left-4 top-1/2 -translate-y-1/2 text-sm text-[#4A4A4A] pointer-events-none select-none">$</span>
      )}
      <input
        type="text"
        inputMode="decimal"
        value={value}
        onChange={(e) => onChange(e.target.value.replace(/[^0-9.]/g, ""))}
        placeholder={placeholder}
        className={inputCls + (value ? " pl-8" : "")}
        data-testid={testId}
      />
    </div>
  );
}

function NewBookingPanel({ onClose, onDone }) {
  const [cats, setCats] = useState([]);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    first_name: "", last_name: "", email: "", phone: "", category: "",
    session_date: "", session_time: "", duration: "", session_location: "",
    price: "", amount_paid: "", payment_method: "", notes: "", send_welcome_email: true,
  });
  useEffect(() => { api.get("/categories").then((r) => setCats(r.data)).catch(() => {}); }, []);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const total = parseFloat(form.price) || 0;
  const paid = parseFloat(form.amount_paid) || 0;
  const balance = Math.max(total - paid, 0);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/admin/bookings", { ...form, amount_paid: paid });
      toast.success("Client added — the session is on your calendar");
      onDone();
      onClose();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="booking-add-panel">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative bg-white w-full max-w-md h-full overflow-y-auto p-6 shadow-xl">
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-serif text-2xl">New Client / Booking</h3>
          <button onClick={onClose} data-testid="booking-add-close"><X size={18} /></button>
        </div>
        <p className="text-xs text-[#7A7A7A] mb-6">After saving, the session appears on your Calendar automatically and you can send the agreement from the client record.</p>
        <form onSubmit={submit} className="space-y-3" data-testid="booking-add-form">
          <div className="grid grid-cols-2 gap-3">
            <input value={form.first_name} onChange={set("first_name")} required placeholder="First name *" className={inputCls} data-testid="nb-first-name" />
            <input value={form.last_name} onChange={set("last_name")} placeholder="Last name" className={inputCls} data-testid="nb-last-name" />
          </div>
          <input type="email" value={form.email} onChange={set("email")} required placeholder="Email *" className={inputCls} data-testid="nb-email" />
          <input value={form.phone} onChange={set("phone")} placeholder="Phone" className={inputCls} data-testid="nb-phone" />
          <div className="grid grid-cols-2 gap-3">
            <select value={form.category} onChange={set("category")} className={inputCls} data-testid="nb-category">
              <option value="">Photography type…</option>
              {cats.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
            </select>
            <input value={form.session_location} onChange={set("session_location")} placeholder="Location / address" className={inputCls} data-testid="nb-location" />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <input type="date" value={form.session_date} onChange={set("session_date")} className={inputCls} data-testid="nb-date" />
            <select value={form.session_time} onChange={set("session_time")} className={inputCls} data-testid="nb-time">
              <option value="">Time</option>
              {TIME_OPTIONS.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
            <select value={form.duration} onChange={set("duration")} className={inputCls} data-testid="nb-duration">
              <option value="">Duration</option>
              {DURATION_OPTIONS.map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <MoneyField value={form.price} onChange={(v) => setForm({ ...form, price: v })} placeholder="Total price" testId="nb-price" />
            <MoneyField value={form.amount_paid} onChange={(v) => setForm({ ...form, amount_paid: v })} placeholder="Amount paid" testId="nb-paid" />
          </div>
          <div className="flex items-center gap-3">
            <select value={form.payment_method} onChange={set("payment_method")} className={`${inputCls} flex-1`} data-testid="nb-method">
              <option value="">Payment method…</option>
              {PAYMENT_METHODS.map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
            <p className="text-sm text-[#4A4A4A] whitespace-nowrap" data-testid="nb-balance-preview">
              Balance: <span className="font-medium">${balance.toFixed(0)}</span>
            </p>
          </div>
          <textarea value={form.notes} onChange={set("notes")} rows={3} placeholder="Internal notes…" className={`${inputCls} resize-y`} data-testid="nb-notes" />
          <label className="flex items-center gap-3 text-sm text-[#4A4A4A] cursor-pointer border border-[#E5E5E2] px-4 py-3 hover:border-[#1A1A1A] transition-colors" data-testid="nb-welcome-wrap">
            <input type="checkbox" checked={form.send_welcome_email} onChange={(e) => setForm({ ...form, send_welcome_email: e.target.checked })}
              className="accent-[#1A1A1A] w-4 h-4" data-testid="nb-welcome-email" />
            Email the client a welcome note with their personal session page link
          </label>
          <button type="submit" disabled={saving} data-testid="nb-save"
            className="w-full bg-[#1A1A1A] text-white text-xs uppercase tracking-[0.15em] py-3 hover:bg-[#4A4A4A] disabled:opacity-50">
            {saving ? "Saving…" : "Save Client & Booking"}
          </button>
        </form>
      </div>
    </div>
  );
}

export default function RequestsTab({ endpoint, statuses, nameOf, details, testId, recordHref }) {
  const [items, setItems] = useState([]);
  const [draftNotes, setDraftNotes] = useState({});
  const [adding, setAdding] = useState(false);
  const [filter, setFilter] = useState("all");

  const load = () => api.get(endpoint).then((r) => setItems(r.data)).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); }, [endpoint]);

  const visible = items.filter((r) => filter === "all" || (filter === "manual" ? r.source === "manual" : r.source !== "manual"));

  const save = async (id, patch) => {
    try {
      const r = await api.put(`${endpoint}/${id}`, patch);
      setItems((arr) => arr.map((x) => (x.id === id ? r.data : x)));
      toast.success("Saved");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div data-testid={`admin-${testId}-list`} className="space-y-4">
      {testId === "bookings" && (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-2" data-testid="bookings-source-filter">
            {[["all", "All"], ["website", "Website Form"], ["manual", "Added by You"]].map(([v, l]) => (
              <button key={v} onClick={() => setFilter(v)} data-testid={`bookings-filter-${v}`}
                className={`text-[11px] uppercase tracking-[0.12em] px-4 py-2 border transition-colors ${filter === v ? "bg-[#1A1A1A] text-white border-[#1A1A1A]" : "border-[#E5E5E2] hover:border-[#1A1A1A]"}`}>
                {l} ({items.filter((r) => v === "all" || (v === "manual" ? r.source === "manual" : r.source !== "manual")).length})
              </button>
            ))}
          </div>
          <button onClick={() => setAdding(true)} data-testid="booking-add-btn"
            className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.12em] bg-[#1A1A1A] text-white px-5 py-2.5 hover:bg-[#4A4A4A]">
            <Plus size={13} /> Add New Client / Booking
          </button>
        </div>
      )}
      {visible.length === 0 && <p className="text-sm text-[#7A7A7A] py-10 text-center">No requests yet.</p>}
      {visible.map((r) => (
        <div key={r.id} data-testid={`admin-${testId}-item-${r.id}`} className="bg-white border border-[#E5E5E2] p-6">
          <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
            <div>
              <p className="font-medium text-base">{nameOf(r)}
                {testId === "bookings" && (
                  <span className={`ml-2 text-[9px] uppercase tracking-[0.15em] px-2 py-0.5 align-middle ${r.source === "manual" ? "bg-[#EFEFEC] text-[#4A4A4A]" : "bg-[#E7E3F2] text-[#4A4A4A]"}`}
                    data-testid={`admin-bookings-source-${r.id}`}>
                    {r.source === "manual" ? "Added by you" : "Website form"}
                  </span>
                )}
              </p>
              <p className="text-sm text-[#4A4A4A]">{r.email} · {r.phone}</p>
              <p className="text-xs text-[#7A7A7A] mt-1">{new Date(r.created_at).toLocaleString()}</p>
            </div>
            <select value={r.status} onChange={(e) => save(r.id, { status: e.target.value })}
              data-testid={`admin-${testId}-status-${r.id}`}
              className={`text-xs uppercase tracking-[0.1em] px-3 py-2 border border-[#E5E5E2] outline-none ${STATUS_COLORS[r.status] || ""}`}>
              {statuses.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          {recordHref && (
            <a href={recordHref(r)} data-testid={`admin-${testId}-record-${r.id}`}
              className="inline-block mb-4 text-[11px] uppercase tracking-[0.15em] bg-[#1A1A1A] text-white px-5 py-2.5 hover:bg-[#4A4A4A] transition-colors">
              Open Client Record — Forms & Contract
            </a>
          )}
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-2 mb-4">
            {details(r).filter(([, v]) => v).map(([k, v]) => (
              <p key={k} className="text-sm"><span className="text-[#7A7A7A] text-xs uppercase tracking-[0.1em] mr-2">{k}</span>{v}</p>
            ))}
          </div>
          <div className="flex gap-3 items-start">
            <textarea rows={2} placeholder="Internal notes…" value={draftNotes[r.id] ?? r.notes ?? ""}
              onChange={(e) => setDraftNotes({ ...draftNotes, [r.id]: e.target.value })}
              data-testid={`admin-${testId}-notes-${r.id}`}
              className="flex-1 border border-[#E5E5E2] px-3 py-2 text-sm outline-none focus:border-[#1A1A1A] resize-none" />
            <button onClick={() => save(r.id, { notes: draftNotes[r.id] ?? r.notes ?? "" })}
              data-testid={`admin-${testId}-save-notes-${r.id}`}
              className="text-xs uppercase tracking-[0.15em] bg-[#1A1A1A] text-white px-5 py-2.5 hover:bg-[#4A4A4A] transition-colors">
              Save
            </button>
          </div>
        </div>
      ))}
      {adding && <NewBookingPanel onClose={() => setAdding(false)} onDone={load} />}
    </div>
  );
}
